import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  bundleByProduct,
  categoryVocabulary,
  formatCents,
  isOrganicStore,
  newId,
  relevanceScore,
  sizeLabel,
  slugify,
  variantLabel,
  type Bundle,
  type CompareResponse,
  type CreateVariantResponse,
  type LiveResultDto,
  type LiveSearchResponse,
  type LocalSearchResponse,
  type SearchResultDto,
  type StoreCode,
} from '@familyhub/shared';
import { api, isOffline } from '../api/client';
import {
  db,
  getMeta,
  setMeta,
  type GenericProductRecord,
  type ListItemRecord,
  type ProductVariantRecord,
  type StoreRecord,
  type SuggestionRecord,
  storesByName,
} from '../db';
import { stamp } from '../db/clock';
import { enqueue } from '../db/outbox';
import {
  acceptSuggestion,
  addItem,
  hideVariant,
  adjustQuantity,
  moveToStore,
  removeItem,
  setNote,
} from '../list/actions';
import { imagesByGeneric } from '../list/images';
import { shelfTitle, shopLogo, shopProblem } from '../list/shops';
import { sync } from '../sync/engine';
import { ProductThumb } from '../components/ProductThumb';
import { OrganicLeaf } from '../components/OrganicLeaf';
import { Tile } from '../components/Tile';

/**
 * Adding something (ARCHITECTURE §5.1, §6.4).
 *
 * Local first: anything the household has chosen before is served from
 * IndexedDB, instantly, offline, and ranked above anything live. Only when the
 * local catalogue has nothing does the app ask the shops — which is the
 * "live search, not bulk sync" trade in one interaction.
 *
 * Selection is what persists a live hit (API §4.4). Until then nothing about it
 * is stored, so a browse costs the catalogue nothing.
 *
 * **The screen does not close when something is added.** A weekly shop is
 * fifteen items, and it used to be fifteen round trips out to the list and back
 * in through the button: the field clears, keeps focus, and says what it just
 * did. You leave when you are finished, not once per item.
 *
 * **And it no longer asks which shop.** Every add was interrupted by a row of
 * store chips before the item existed at all. The answer is nearly always
 * derivable — a live hit belongs to the shop it was found in, a variant already
 * carries its store, and a bare generic goes where the household usually buys
 * it — so it is derived, stated in the confirmation, and corrected in one tap
 * from the item's own sheet on the list. Any stays a first-class outcome; it
 * just stopped costing a decision every single time.
 */

/**
 * What to say when the shops cannot be reached.
 *
 * The old wording — "showing local results only" — was written for the case
 * where there *are* local results. Said over an empty screen it describes
 * something the person can plainly see is not happening, which reads as the app
 * being confused rather than offline. With nothing local the useful sentence is
 * the one that names the way forward, and adding by hand is right there.
 */
function offlineMessage(localCount: number): string {
  return localCount > 0
    ? 'Geen verbinding — je ziet wat er al op deze telefoon staat.'
    : 'Geen verbinding, dus de winkels kunnen niet doorzocht worden. Je kunt het nog steeds zelf toevoegen.';
}

/**
 * One shop's product offered on the just-added card. Built from a compare
 * answer when there is a connection, from this phone's own variants when not —
 * so `variantId` is null only for a shop product the household never chose.
 */
interface PickOption {
  key: string;
  variantId: string | null;
  externalId: string | null;
  store: StoreCode;
  name: string;
  brand: string | null;
  imagePath: string | null;
  priceCents: number | null;
}

/** The shops' shelves, always in this order (the household's, 2026-10-17). */
const SHELF_ORDER: StoreCode[] = ['lidl', 'ah', 'ekoplaza', 'jumbo'];

interface Cheaper {
  store: string;
  price: number;
}

/**
 * The cheapest *other* shop selling the same product, when it is strictly
 * cheaper than this one — or null. Null for a row with no price of its own:
 * "goedkoper" than a price nobody knows is not a true sentence.
 */
function cheaperElsewhere(item: LiveResultDto, bundle: Bundle<LiveResultDto> | undefined): Cheaper | null {
  const own = item.promo_price_cents ?? item.price_cents;
  if (own === null || !bundle) return null;
  let best: Cheaper | null = null;
  for (const offer of bundle.offers) {
    if (offer.store === item.store || offer.price_cents === null) continue;
    if (offer.price_cents < own && (!best || offer.price_cents < best.price)) {
      best = { store: offer.store, price: offer.price_cents };
    }
  }
  return best;
}

/** Products this phone was told "maakt niet uit" for — never offered picks again. */
const ANY_STORE_META = 'add_any_store_generics';

type Pending =
  | {
      kind: 'local';
      genericId: string;
      variantId: string | null;
      label: string;
      storeId: string | null;
      imagePath: string | null;
    }
  | { kind: 'live'; product: LiveResultDto }
  | { kind: 'manual'; name: string };

/**
 * What was just added — the anchor for the card under the search field.
 *
 * An **id**, never a copy of the row, for the same reason the list's detail
 * sheet holds one: the card carries a quantity stepper, so a snapshot would go
 * stale the moment the stepper did its job, and it would also miss what the
 * other phone has since done to the same item.
 */
interface Added {
  itemId: string;
  name: string;
  where: string;
  imagePath: string | null;
}

/**
 * One row of the household's own catalogue: the product, and which of its
 * variants this row is about. The shape both matchers produce — the server's
 * ranked full-text search and the phone's own substring one — so that
 * everything past the match is written once.
 */
interface OwnRow {
  generic: GenericProductRecord;
  variant: ProductVariantRecord | null;
}

export function AddScreen() {
  const [query, setQuery] = useState('');
  const [live, setLive] = useState<LiveSearchResponse | null>(null);
  /** The server's ranking of the household's own catalogue; null offline. */
  const [catalogueHits, setCatalogueHits] = useState<SearchResultDto[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [added, setAdded] = useState<Added | null>(null);
  /** Whether the note field on that card is open, and what is in it. */
  const [noting, setNoting] = useState(false);
  const [noteText, setNoteText] = useState('');
  /**
   * The note as last written to the outbox, so the field can commit on blur
   * *and* on its own button without queueing the same whole-row write twice.
   * Every patch is a full row under per-row LWW, so a duplicate is not free.
   */
  const savedNote = useRef<string>('');
  /**
   * Show only organic (biologisch) products, across the household's own
   * catalogue and the shops alike.
   *
   * Filtered on the client, from the `organic` flag mirrored onto every row
   * (migration 019) and carried on every live hit — so it works with no signal,
   * exactly like the store filter beside it, and for the same reason: it is a
   * person narrowing what is already on the screen, not the model dropping rows.
   */
  const [organicOnly, setOrganicOnly] = useState(false);
  const field = useRef<HTMLInputElement>(null);

  const stores = useLiveQuery(storesByName, [], [] as StoreRecord[]);

  const suggestions = useLiveQuery(
    async () => await db.suggestions.orderBy('confidence').reverse().limit(6).toArray(),
    [],
    [] as SuggestionRecord[],
  );

  const catalogue = useLiveQuery(async () => {
    const [generics, variants, history, items] = await Promise.all([
      db.generic_products.toArray(),
      db.product_variants.toArray(),
      db.history.toArray(),
      db.list_items.toArray(),
    ]);
    return { generics, variants, history, items };
  }, []);

  /**
   * A picture for each generic, borrowed from one of its own variants. Every
   * tile on this screen above the live results is a generic, and a generic has
   * no photograph of its own — without this the two sections the household
   * uses most were a grid of lettered squares.
   */
  const genericImages = useMemo(
    () => imagesByGeneric(catalogue?.variants ?? []),
    [catalogue],
  );

  /** Which generics are organic, for the tiles that show a generic (recent, suggestions). */
  const genericOrganic = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const g of catalogue?.generics ?? []) m.set(g.id, !!g.organic);
    return m;
  }, [catalogue]);

  /**
   * Whether an own-catalogue row is organic: the specific variant when there is
   * one, otherwise the generic (a "biologische …" entered by hand).
   */
  const rowIsOrganic = (row: OwnRow): boolean =>
    !!(row.variant?.organic || row.generic.organic);

  /** The live rows of the list, per product, so a tile knows what it would join. */
  const listed = useMemo(() => {
    const byGeneric = new Map<string, ListItemRecord[]>();
    for (const item of catalogue?.items ?? []) {
      if (item.deleted_at) continue;
      const rows = byGeneric.get(item.generic_product_id) ?? [];
      rows.push(item);
      byGeneric.set(item.generic_product_id, rows);
    }
    return byGeneric;
  }, [catalogue]);

  /**
   * The one row a second tap on this product should join, if there is one.
   *
   * Tapping something already on the list used to be refused — the tile went
   * grey with "op de lijst" on it, which is a dead end at exactly the moment
   * somebody is saying they want another one. So a second tap is *one more*,
   * which is what a person tapping the same picture twice means.
   *
   * It refuses in the two cases where "one more of that" has no single answer:
   * with two rows for the same product on the list there is no saying which was
   * meant, and a tap on a **different** shop's carton of something already
   * there is a new product rather than a second unit of the old one. Both fall
   * back to adding a row, which is what happened before this existed.
   *
   * A generic tile — "Havermelk", no variant — joins whatever row is there,
   * including one that carries a specific carton: the household asked for
   * another oat milk, and the one on the list is the oat milk they buy.
   */
  function existingRow(genericId: string, variantId: string | null): ListItemRecord | null {
    const rows = listed.get(genericId) ?? [];
    if (rows.length !== 1) return null;
    const row = rows[0]!;
    if (variantId && row.variant_id && row.variant_id !== variantId) return null;
    return row;
  }

  /**
   * How a tile should present itself given what is already on the list.
   *
   * Three states, and only one of them is inert. Not on the list: an ordinary
   * add. On it once: the count is the badge and a tap is one more, which is why
   * the accessible name has to say so — "Melk toevoegen" over a tile marked
   * **2** is a button lying about what it does. On it more than once: there is
   * no single row to add to, so this stays the wall it always was, and the list
   * screen is where an ambiguity like that gets sorted out.
   */
  function tileState(genericId: string, variantId: string | null, name: string) {
    const rows = listed.get(genericId) ?? [];
    const already = existingRow(genericId, variantId);
    if (already) {
      return {
        label: `Nog één ${name} toevoegen, nu ${already.quantity} op de lijst`,
        badge: String(already.quantity),
        disabled: false,
      };
    }
    return {
      label: `${name} toevoegen`,
      badge: rows.length > 0 ? 'op de lijst' : null,
      disabled: rows.length > 0,
    };
  }

  /**
   * The row the card is about, read live rather than remembered.
   *
   * Three states, and they are deliberately not two: `undefined` is Dexie not
   * having answered yet — the write happened microseconds ago, so the card
   * shows the one unit that was just added rather than blinking out and back —
   * and `null` means the row is genuinely gone, which is what "Ongedaan maken"
   * or the other phone leaves behind.
   */
  const addedRow = useLiveQuery(async () => {
    if (!added) return null;
    const row = await db.list_items.get(added.itemId);
    return row && !row.deleted_at ? row : null;
  }, [added?.itemId]);

  const addedQuantity = addedRow?.quantity ?? 1;

  /**
   * "Welke wordt het?" — up to three shops' products on the card, for an add
   * that went on without one (ARCHITECTURE §5.1: the catalogue grows by
   * selection, and this is the moment the person is already looking).
   *
   * A nudge, and built so it cannot become a nag: nothing is asked, the card
   * still goes the moment the next word is typed, a tap anywhere else ignores
   * it, and "Maakt niet uit" is remembered per product so bananen from the
   * market are never offered a carton again. The options are the comparison
   * view's own answer — relevance decides what appears, price the order, the
   * household's usual product first — so the card cannot recommend something
   * the compare screen would not. Offline it offers only the household's own
   * products, which can be chosen with no signal; with none, nothing is shown.
   */
  const [picks, setPicks] = useState<{ itemId: string; options: PickOption[] } | null>(null);
  const [choosing, setChoosing] = useState(false);
  /** "Misschien niet wat je zocht" — folded until asked for, per search. */
  const [asideOpen, setAsideOpen] = useState(false);
  /** The "Jullie producten" tile held down, whose sheet offers to hide it. */
  const [hiding, setHiding] = useState<{ variantId: string; name: string; where: string | null } | null>(null);
  useEffect(() => setAsideOpen(false), [query]);
  const pickFor = added && addedRow && !addedRow.variant_id ? addedRow : null;

  useEffect(() => {
    if (!pickFor) return;
    const itemId = pickFor.id;
    const genericId = pickFor.generic_product_id;
    let current = true;
    void (async () => {
      const anyStore = (await getMeta<string[]>(ANY_STORE_META)) ?? [];
      if (anyStore.includes(genericId)) return;
      let options: PickOption[];
      try {
        const found = await api<CompareResponse>(
          `/api/v1/compare?generic_id=${genericId}&limit=20`,
        );
        options = found.results.map((r) => ({
          key: r.variant.id ?? `${r.variant.store}:${r.variant.external_id}`,
          variantId: r.variant.id,
          externalId: r.variant.external_id,
          store: r.variant.store,
          name: r.variant.name,
          brand: r.variant.brand,
          imagePath: r.variant.image_path,
          priceCents: r.price_cents,
        }));
      } catch {
        const stores = await db.stores.toArray();
        options = (await db.product_variants.where('generic_product_id').equals(genericId).toArray())
          .filter((v) => !v.deleted_at)
          .flatMap((v) => {
            const store = stores.find((st) => st.id === v.store_id);
            return store
              ? [{
                  key: v.id,
                  variantId: v.id,
                  externalId: v.external_id,
                  store: store.code,
                  name: v.name,
                  brand: v.brand,
                  imagePath: v.image_path ?? null,
                  priceCents: null,
                }]
              : [];
          });
      }
      // Only what can actually be chosen: a shop product needs its article
      // number for selection, and a shop this phone does not know has no id.
      // Then **one per shop** — the first, which in the compare order is the
      // household's usual product or else the cheapest — so three tiles are a
      // choice between shops, not three sizes of one shop's jar.
      const seenStore = new Set<string>();
      options = options
        .filter((o) => o.variantId || o.externalId)
        .filter((o) => !seenStore.has(o.store) && seenStore.add(o.store))
        .slice(0, 3);
      if (current) setPicks({ itemId, options });
    })();
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickFor?.id]);

  async function choosePick(option: PickOption) {
    const target = added;
    if (!target || !pickFor) return;
    setChoosing(true);
    setProblem(null);
    try {
      let variantId = option.variantId;
      let imagePath = option.imagePath;
      if (!variantId) {
        // Attached to the product already on the list, not a new one: the
        // person has just said this carton is what they meant by it.
        const created = await api<CreateVariantResponse>('/api/v1/variants', {
          method: 'POST',
          body: {
            id: newId(),
            store: option.store,
            external_id: option.externalId,
            generic_product_id: pickFor.generic_product_id,
            name: option.name,
            brand: option.brand,
            price_cents: option.priceCents,
          },
        });
        variantId = created.variant.id;
        imagePath = created.variant.image_path ?? imagePath;
      }
      // The variant and its store move together, always.
      const storeId = storeOf(option.store)?.id ?? null;
      await moveToStore(target.itemId, storeId, variantId);
      setAdded({ ...target, where: nameOfStore(storeId), imagePath: imagePath ?? target.imagePath });
      setPicks(null);
      void sync();
    } catch {
      setProblem('Dat product kon niet gekozen worden. Het staat er nog zonder winkel op.');
    } finally {
      setChoosing(false);
      field.current?.focus();
    }
  }

  async function anyStorePick() {
    if (!pickFor) return;
    const anyStore = (await getMeta<string[]>(ANY_STORE_META)) ?? [];
    if (!anyStore.includes(pickFor.generic_product_id)) {
      await setMeta(ANY_STORE_META, [...anyStore, pickFor.generic_product_id]);
    }
    setPicks(null);
    field.current?.focus();
  }

  const visiblePicks =
    pickFor && picks && picks.itemId === pickFor.id && picks.options.length > 0 ? picks.options : null;

  /** Every live variant the household holds, per generic. */
  const variantsOf = useMemo(() => {
    const out = new Map<string, ProductVariantRecord[]>();
    for (const v of catalogue?.variants ?? []) {
      // Hidden from "Jullie producten" (migration 041).
      if (v.deleted_at || v.hidden) continue;
      const list = out.get(v.generic_product_id) ?? [];
      list.push(v);
      out.set(v.generic_product_id, list);
    }
    return out;
  }, [catalogue]);

  /**
   * How a matched product becomes rows on the screen — one rule, both matchers.
   *
   * **Only products a supermarket sells are shown** (asked for 2026-10-17).
   * There used to be a "this product, whichever one" tile beside its cartons —
   * a lettered square for a product with no variant, or a second copy of the
   * carton's own photograph under the provisional generic name — and it was
   * the one tile on the screen that put nothing a shop sells on the list. So a
   * matched *name* now stands for its cartons: every variant of it, the ones
   * whose own title matched first. A name with no carton at all has no tile;
   * "zelf toevoegen" above still adds it, and finds the existing product
   * rather than a duplicate.
   */
  function composeRows(
    generic: GenericProductRecord,
    hits: ProductVariantRecord[],
    genericMatched: boolean,
  ): OwnRow[] {
    const variants = genericMatched
      ? [...hits, ...(variantsOf.get(generic.id) ?? []).filter((v) => !hits.includes(v))]
      : hits;
    return variants.map((variant) => ({ generic, variant }));
  }

  /**
   * The offline matcher: substring plus slug, over the Dexie mirror.
   *
   * It is one of **two** matchers behind one shape, not the only one — see
   * `serverResults`. What it is for is answering with no connection at all,
   * over a household-sized catalogue held in memory.
   */
  const localResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !catalogue) return [];
    // "Ranked by purchase history first — a variant bought eleven times
    // outranks a lexical near-match" (API §4.1). The counts are cached, so this
    // holds with no signal, which is the half that was missing.
    const historyOf = new Map(catalogue.history.map((h) => [h.generic_product_id, h]));
    const variantsByGeneric = new Map<string, typeof catalogue.variants>();
    for (const v of catalogue.variants) {
      // Hidden by the household (migration 041): gone from this section, and
      // only from this section.
      if (v.deleted_at || v.hidden) continue;
      const list = variantsByGeneric.get(v.generic_product_id) ?? [];
      list.push(v);
      variantsByGeneric.set(v.generic_product_id, list);
    }
    // Slug as well as raw text: `slugify` folds diacritics, so a household that
    // stored "Crème fraîche" is still found by someone typing "creme fraiche"
    // one-handed in a shop. Without this the product hides, the person adds it
    // "by hand", and the server rejects the duplicate slug — a failure whose
    // cause is invisible from the screen it happened on.
    const qs = slugify(query);
    const matches = (name: string) =>
      name.toLowerCase().includes(q) || (qs.length > 0 && slugify(name).includes(qs));

    return catalogue.generics
      .filter((g) => !g.deleted_at)
      .flatMap((g) => {
        const variants = variantsByGeneric.get(g.id) ?? [];
        return composeRows(g, variants.filter((v) => matches(v.name)), matches(g.name));
      })
      .sort((a, b) => {
        const ha = historyOf.get(a.generic.id)?.purchase_count ?? 0;
        const hb = historyOf.get(b.generic.id)?.purchase_count ?? 0;
        // The thing you buy every week is the first hit for a three-letter
        // query typed one-handed in a shop.
        if (ha !== hb) return hb - ha;
        // Then how well the name answers what was typed, rather than the
        // alphabet: searching "sinaasappel" should not put a yoghurt that
        // mentions it above the fruit merely because Y follows S. Same scorer
        // the live results are ranked with, so the two halves of this screen
        // do not disagree about what the query meant.
        const ra = relevanceScore(a.variant?.name ?? a.generic.name, query);
        const rb = relevanceScore(b.variant?.name ?? b.generic.name, query);
        if (ra !== rb) return rb - ra;
        return a.generic.name.localeCompare(b.generic.name);
      })
      .slice(0, 12);
    // `composeRows` reads nothing but its arguments; listing it would re-run
    // this on every render, since it is redeclared each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, catalogue]);

  /**
   * The online matcher: `GET /api/v1/search`, the Dutch full-text search the
   * specs describe (API §4.1) — and which, until now, no client called.
   *
   * That was the expensive state to be in. It is a careful piece of work —
   * `search_tsv` with the Dutch configuration ORed with `pg_trgm` similarity,
   * because the Dutch snowball stemmer has no decompounder and trigram is what
   * actually finds *melk* inside *havermelk* — tested against a real Postgres,
   * cited throughout the documents, and **not what anybody experienced**. Two
   * implementations of one documented feature meant every future fix had to be
   * made twice, or silently was not.
   *
   * So there is one search with two matchers now, and the difference between
   * them is only what a phone can do without a network:
   *
   *   | typed                        | online | offline |
   *   | `havermlek` (a typo)         | found  | nothing |
   *   | `appels` against `Appel`     | found  | nothing |
   *   | `melk` inside `Havermelk`    | found  | found   |
   *   | `creme fraiche` for `Crème`  | found  | found   |
   *
   * Everything downstream is shared: the rows are composed by `composeRows`,
   * rendered by the same tiles, and added by the same local, offline-capable
   * write. The server decides *which* products and in *what order*; it never
   * becomes a dependency of adding one.
   *
   * Rows the server knows and this phone does not are skipped rather than
   * rendered from the response — they are on their way in the next sync, and
   * until then a tile with no picture, no size and no variant to add is worse
   * than the row not being there, which is exactly where it was yesterday.
   */
  const serverResults = useMemo(() => {
    if (!catalogueHits || !catalogue) return null;
    const genericById = new Map(
      catalogue.generics.filter((g) => !g.deleted_at).map((g) => [g.id, g]),
    );
    // Hidden here too: the server's answer may predate a hide this phone has
    // not pushed yet, and what the phone knows is hidden stays hidden.
    const variantById = new Map(
      catalogue.variants.filter((v) => !v.deleted_at && !v.hidden).map((v) => [v.id, v]),
    );

    // The server returns one row per matched variant, or a single variant-less
    // row when only the generic matched. Regrouping restores the shape
    // `composeRows` wants, in the order the server ranked them.
    const order: string[] = [];
    const hits = new Map<string, ProductVariantRecord[]>();
    const bare = new Set<string>();
    for (const row of catalogueHits) {
      // An overlay may not take the screen down. This one arrives from the
      // network, is optional by construction, and the thing underneath it —
      // the offline matcher — is a complete answer on its own, so a response
      // that is not the shape it claims to be costs the ranking rather than
      // the ability to add anything.
      const id = row?.generic_product?.id;
      if (!id || !genericById.has(id)) continue;
      if (!hits.has(id)) {
        hits.set(id, []);
        order.push(id);
      }
      const variant = row.variant ? variantById.get(row.variant.id) : undefined;
      if (variant) hits.get(id)!.push(variant);
      else bare.add(id);
    }

    return order
      .flatMap((id) => composeRows(genericById.get(id)!, hits.get(id)!, bare.has(id)))
      .slice(0, 12);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogueHits, catalogue]);

  /**
   * What the "Jullie producten" section shows. The server's ranking when there
   * is one, this phone's when there is not — never interleaved, because two
   * rankings interleaved is a third ranking nobody designed.
   *
   * **But the server's answer may only add rows, never take them away.** The
   * phone's answer is on screen first, and the server's used to replace it
   * wholesale — so whatever the server's matcher missed (it did not find
   * "melk" inside Havermelk or "creme" in Crème fraîche) appeared, then
   * vanished while the shops were still being asked. What only the phone
   * found now follows the server's rows, in the phone's order.
   *
   * Narrowed to organic when the filter is on, which is a plain post-hoc
   * filter and so still works offline.
   */
  const ownResults = useMemo(() => {
    const key = (r: { generic: { id: string }; variant: { id: string } | null }) =>
      `${r.generic.id}-${r.variant?.id ?? 'g'}`;
    let base = localResults;
    if (serverResults) {
      const seen = new Set(serverResults.map(key));
      base = [...serverResults, ...localResults.filter((r) => !seen.has(key(r)))].slice(0, 12);
    }
    return organicOnly ? base.filter(rowIsOrganic) : base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverResults, localResults, organicOnly]);

  /**
   * What the household buys, most recently bought first — shown before a single
   * letter is typed.
   *
   * Most adds are repeats, and the screen used to be blank until you typed. The
   * counts and dates are already cached for offline ranking, so this costs no
   * request and works with no signal.
   */
  const recent = useMemo(() => {
    if (!catalogue) return [];
    const genericById = new Map(catalogue.generics.filter((g) => !g.deleted_at).map((g) => [g.id, g]));
    return catalogue.history
      .filter((h) => genericById.has(h.generic_product_id))
      .filter((h) => !organicOnly || genericOrganic.get(h.generic_product_id))
      .sort((a, b) => {
        const ta = a.last_purchased_at ? Date.parse(a.last_purchased_at) : 0;
        const tb = b.last_purchased_at ? Date.parse(b.last_purchased_at) : 0;
        if (ta !== tb) return tb - ta;
        return b.purchase_count - a.purchase_count;
      })
      .slice(0, 12)
      .map((h) => ({ history: h, generic: genericById.get(h.generic_product_id)! }));
  }, [catalogue, organicOnly, genericOrganic]);

  /**
   * The specific product a bare generic resolves to when it is added.
   *
   * Nudging the household towards a real carton without asking anything: most
   * adds are repeats, and the server already knows which product they buy under
   * each name (`usual_variant_id` on `GET /history`). So a tap on "Melk" puts
   * *their* milk on the list — with its shop, its picture and its price — and
   * the card says which, so a wrong guess is one tap away in the item's sheet.
   * Only a variant this phone holds and nobody removed qualifies; anything else
   * stays generic, because no shop beats the wrong shop.
   */
  const usualVariant = useMemo(() => {
    const out = new Map<string, ProductVariantRecord>();
    if (!catalogue) return out;
    const variants = new Map(
      catalogue.variants.filter((v) => !v.deleted_at && !v.hidden).map((v) => [v.id, v]),
    );
    for (const h of catalogue.history) {
      const variant = h.usual_variant_id ? variants.get(h.usual_variant_id) : undefined;
      if (variant && variant.generic_product_id === h.generic_product_id) {
        out.set(h.generic_product_id, variant);
      }
    }
    return out;
  }, [catalogue]);

  /** A new row for a generic: its usual product if it has one, else Overal. */
  async function addGeneric(genericId: string, name: string, picture: string | null) {
    const usual = usualVariant.get(genericId);
    const storeId = usual?.store_id ?? null;
    const itemId = await addItem({ genericProductId: genericId, variantId: usual?.id ?? null, storeId });
    readyForNext({
      itemId,
      name,
      where: nameOfStore(storeId),
      imagePath: usual?.image_path ?? picture,
    });
  }

  const storeOf = (code: string) => stores.find((s) => s.code === code) ?? null;
  const nameOfStore = (id: string | null) =>
    id ? (stores.find((s) => s.id === id)?.name ?? 'de winkel') : 'Overal';
  const storeName = (code: string) => storeOf(code)?.name ?? code;
  const hideTarget = (name: string, variant: ProductVariantRecord) => ({
    variantId: variant.id,
    name: variant.name || name,
    where: stores.find((s) => s.id === variant.store_id)?.name ?? null,
  });

  /**
   * One row per product rather than one per shop.
   *
   * Four shops answering the same question put the identical carton of Oatly in
   * the column three times, rows apart, and the only way to find the cheaper
   * one was to scroll and hold three numbers in your head — on the screen where
   * the choice is actually made, rather than in the comparison view, which is a
   * tap further on and one product at a time.
   *
   * `bundleByProduct` regroups; it never re-orders. A bundle sits where its
   * best-ranked member sat, so the server's relevance and verdict ordering
   * arrives here intact, and a product only one shop returned comes back as a
   * bundle of one and renders exactly as it always did.
   */
  /**
   * The words this household uses to name a kind of product.
   *
   * One shop prints the category in a title and another does not — `Doritos
   * Nacho cheese` against `Doritos Nacho Cheese Tortilla Chips`, the same 170 g
   * bag as two rows — and telling a category apart from a qualifier is the
   * whole difficulty, because a rule that allowed any extra words would also
   * merge regular oat milk into barista. The household's own generic products
   * are the answer that is derived rather than configured (ARCHITECTURE §3.3),
   * and it gets better as the catalogue grows.
   */
  const categories = useMemo(
    () =>
      categoryVocabulary(
        (catalogue?.generics ?? []).filter((g) => !g.deleted_at).map((g) => g.name),
      ),
    [catalogue],
  );

  /**
   * The shops' answer, narrowed to one shop if that was asked for.
   *
   * Filtering happens **before** bundling rather than inside the rendered
   * bundles, because a bundle is a claim about several shops: leave it whole
   * and a Jumbo-only screen still prints an Albert Heijn price with
   * "goedkoopst" on it, which is the one thing this app must never say by
   * accident. Bundling one shop's rows produces bundles of one, and
   * `bundleByProduct` never re-orders, so the server's ranking survives.
   *
   * This is not the filtering API §4.2 forbids. That rule is about the *model*
   * silently dropping rows nobody asked it to; this is a person choosing, it
   * says which shop is chosen, and "Alle winkels" is one tap away.
   */
  const visibleResults = useMemo(() => {
    let rows = live?.results ?? [];
    // Organic narrowing is a person's choice on rows already fetched, so it
    // filters here rather than re-asking the shops — the same reasoning as the
    // store filter above (API §4.2 forbids the *model* dropping rows, not this).
    if (organicOnly) rows = rows.filter((r) => r.organic);
    return rows;
  }, [live, organicOnly]);

  const bundles = useMemo(
    () => bundleByProduct(visibleResults, { categories }),
    [visibleResults, categories],
  );

  /**
   * Two lists, because two of the four shops answer a word they do not stock
   * with an arbitrary page of whatever is on promotion.
   *
   * Searching `nutella`, Lidl returned DORITOS, COCA-COLA, karnemelk, rucola
   * and a melon; `oatly` returned a storage trolley, a pencil case and an
   * air fryer. Sorting those to the bottom of one list — which is all the
   * model's verdict used to do — still leaves half the screen reading as a bug
   * in *this* app rather than in the shop.
   *
   * So they get their own heading, and **nothing is dropped**: a row under it
   * is one tap away, which is the whole reason this is a heading rather than a
   * filter (API §4.2). A bundle goes below only when every one of its offers
   * was judged unrelated — one shop's junk cannot demote a product another shop
   * answered properly — and with no verdicts at all the second list is empty
   * and the screen is exactly what it was.
   */
  const sections = useMemo(() => {
    const aside: Bundle<LiveResultDto>[] = [];
    for (const bundle of bundles) {
      const noise =
        bundle.offers.length > 0 &&
        bundle.offers.every((offer) => offer.item.relevance === 'unrelated');
      if (noise) aside.push(bundle);
    }
    // Which products are on the page in more than one package. Not a merge —
    // a 2 L bottle and a 4 × 500 ml pack are different products at different
    // prices — but worth saying, rather than rendering them as strangers.
    const families = new Map<string, number>();
    for (const bundle of bundles) {
      if (bundle.family) families.set(bundle.family, (families.get(bundle.family) ?? 0) + 1);
    }
    return { aside, families };
  }, [bundles]);

  /**
   * The shops' answers as one shelf per shop, in a fixed order (2026-10-17):
   * Lidl, Albert Heijn, Ekoplaza, Jumbo — the household's own order, which
   * never moves with what a search returned, so a thumb learns where each shop
   * is. Each shelf keeps the server's ranking within it.
   *
   * What the model judged unrelated stays off the shelves and in the folded
   * section below: a carousel per shop would otherwise put Lidl's flyer
   * (pans, a sandbox) on show as that shop's answer. The rule is the
   * bundle's, so one shop's junk cannot pull another shop's real answer off.
   */
  const shelves = useMemo(() => {
    const bundleOf = new Map<LiveResultDto, Bundle<LiveResultDto>>();
    for (const bundle of bundles) {
      bundleOf.set(bundle.lead, bundle);
      for (const offer of bundle.offers) bundleOf.set(offer.item, bundle);
    }
    const noise = new Set(sections.aside);
    // When the model did not judge the page (no key, a timeout), the shelf
    // would lead with whatever the shop put first — for Lidl, its weekly
    // flyer: a cooking pan for "pindakaas". So a title that does not mention
    // the word at all moves to the end of its shelf. Stable, and never a
    // removal: it is still one swipe away.
    const mentions = (r: LiveResultDto) => (relevanceScore(r.name, query) > 0 ? 0 : 1);
    return SHELF_ORDER.map((code) => ({
      code,
      status: live?.stores[code],
      items: visibleResults
        .filter((r) => r.store === code)
        .filter((r) => !noise.has(bundleOf.get(r)!))
        .sort((a, b) => mentions(a) - mentions(b))
        .map((r) => ({ item: r, cheaper: cheaperElsewhere(r, bundleOf.get(r)) })),
    })).filter((shelf) => shelf.items.length > 0 || (shelf.status && !shelf.status.ok));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundles, sections, visibleResults, live]);

  /**
   * The shops are asked for **every** query of three letters or more.
   *
   * They used to be asked only when the household's own catalogue had nothing,
   * which sounds thrifty and is wrong: owning one kind of yoghurt is not an
   * answer to "what yoghurt is there", and the section that would have shown
   * the other three shops simply never appeared. Your own products still rank
   * above the shops', in their own section, exactly as before — the shops are
   * now underneath them rather than instead of them.
   *
   * It is still one debounced request per typed word, and every adapter behind
   * it keeps the ADAPTERS §4 serial queue and jitter, so this is a couple more
   * requests per shopping trip from one household, not a crawl.
   */
  useEffect(() => {
    setLive(null);
    setCatalogueHits(null);
    setProblem(null);
    const q = query.trim();
    if (q.length < 3) return;

    // Known offline: say so at once rather than waiting out a debounce for a
    // request that cannot succeed. The answer is the same either way, and the
    // person is standing in a shop deciding what to do next.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setProblem(offlineMessage(localResults.length));
      return;
    }

    const timer = window.setTimeout(() => {
      void runLive(q);
      void runCatalogue(q);
    }, 400);
    return () => window.clearTimeout(timer);
    // The local catalogue is no longer a condition of asking, so it is no
    // longer a dependency either — leaving it here would re-run the whole
    // effect, and throw away a good answer, every time Dexie settled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  async function runLive(q: string) {
    setSearching(true);
    setProblem(null);
    try {
      setLive(await api<LiveSearchResponse>(`/api/v1/search/live?q=${encodeURIComponent(q)}`));
    } catch (err) {
      if (isOffline(err)) setProblem(offlineMessage(localResults.length));
      else setProblem('De winkels konden even niet doorzocht worden.');
    } finally {
      setSearching(false);
    }
  }

  /**
   * The household's own catalogue, ranked by the server (API §4.1).
   *
   * Deliberately silent when it fails, and deliberately not part of
   * `runLive`'s try: this is an **overlay on an answer the phone already has**,
   * exactly like shelf prices on the list. Losing it means the substring
   * matcher's ordering rather than the Dutch full-text one — a slightly worse
   * list, not a broken screen — so it must not raise a banner about the shops,
   * and it must not stop the shops being searched. The sync cycle learned this
   * lesson first: a phone that had pulled perfectly still reported "last
   * synced: never" because one overlay threw.
   */
  async function runCatalogue(q: string) {
    try {
      const found = await api<LocalSearchResponse>(
        `/api/v1/search?q=${encodeURIComponent(q)}`,
      );
      setCatalogueHits(found.results);
    } catch {
      setCatalogueHits(null);
    }
  }

  /** Clear down and hand the keyboard back, ready for the next item. */
  function readyForNext(next: Added) {
    setQuery('');
    setLive(null);
    setCatalogueHits(null);
    setBusy(false);
    // A card about a different item starts with an empty note field, and must
    // not carry the previous one's saved value into its first comparison.
    setNoting(false);
    setNoteText('');
    savedNote.current = '';
    setAdded(next);
    field.current?.focus();
  }

  /**
   * One more of something already on the list.
   *
   * `adjustQuantity` rather than a write of `quantity + 1`: it is relative and
   * serialised per row, which is what makes three quick taps on one tile three
   * units instead of two (`list/actions.ts`). Reading the number off the screen
   * and adding one to it is the exact bug that rule exists for, and this screen
   * is now a surface people tap quickly.
   */
  async function addAnother(row: ListItemRecord, name: string, imagePath: string | null) {
    await adjustQuantity(row.id, +1);
    readyForNext({ itemId: row.id, name, where: nameOfStore(row.store_id), imagePath });
  }

  /**
   * Put the note away, saving it if it changed.
   *
   * Once, not per keystroke: every edit to a list item is a whole-row write
   * under per-row last-write-wins, so a save on each letter would be twenty
   * mutations in the outbox for one note — the same reason the list's detail
   * sheet saves on blur. Both the field's blur and its own button land here,
   * and the comparison against what was last written is what keeps that from
   * queueing the identical row twice.
   */
  /**
   * Taking a suggestion. Same card underneath as any other add — it is an add.
   */
  async function accept(suggestion: SuggestionRecord) {
    const picture = genericImages.get(suggestion.generic_product_id) ?? null;
    const already = existingRow(suggestion.generic_product_id, suggestion.suggested_variant_id);
    if (already) {
      // Offered and taken: it has left the list of things you might need,
      // whichever of the two ways it joined the real list.
      await db.suggestions.delete(suggestion.generic_product_id);
      await addAnother(already, suggestion.name, picture);
      return;
    }
    const { itemId, storeId, imagePath } = await acceptSuggestion(suggestion);
    readyForNext({
      itemId,
      name: suggestion.name,
      where: nameOfStore(storeId),
      imagePath: imagePath ?? picture,
    });
  }

  function commitNote() {
    const target = added;
    setNoting(false);
    field.current?.focus();
    if (!target) return;
    const trimmed = noteText.trim();
    if (trimmed === savedNote.current) return;
    savedNote.current = trimmed;
    void setNote(target.itemId, trimmed || null);
  }

  async function commit(target: Pending) {
    setBusy(true);
    setProblem(null);
    try {
      if (target.kind === 'local') {
        const already = existingRow(target.genericId, target.variantId);
        if (already) {
          await addAnother(already, target.label, target.imagePath);
          return;
        }
        // A bare generic becomes the product the household usually buys.
        if (!target.variantId) {
          await addGeneric(target.genericId, target.label, target.imagePath);
          return;
        }
        // A variant carries its own store.
        const storeId = target.storeId;
        const itemId = await addItem({
          genericProductId: target.genericId,
          variantId: target.variantId,
          storeId,
        });
        readyForNext({
          itemId,
          name: target.label,
          where: nameOfStore(storeId),
          imagePath: target.imagePath,
        });
      } else if (target.kind === 'manual') {
        // The household may already have this under a spelling the search did
        // not surface. Adding the existing one is what they meant, and it
        // avoids a `duplicate_slug` rejection they would have to understand
        // from a status screen later.
        const slug = slugify(target.name);
        const existing = (await db.generic_products.toArray()).find(
          (g) => !g.deleted_at && g.slug === slug,
        );
        if (existing) {
          const picture = genericImages.get(existing.id) ?? null;
          const already = existingRow(existing.id, null);
          if (already) {
            await addAnother(already, existing.name, picture);
            return;
          }
          await addGeneric(existing.id, existing.name, picture);
          return;
        }
        // A product created by a person, offline-capable: generic_products is
        // in the sync set, so this works with no connection at all. That is
        // what keeps an adapter-less store like Ekoplaza usable.
        const id = newId();
        await enqueue({
          id: newId(),
          op: 'upsert',
          table: 'generic_products',
          row: {
            id,
            name: target.name,
            slug,
            // Created by a person, so the model may never reclassify it.
            category_source: 'manual',
            updated_at: stamp(),
          },
        });
        const itemId = await addItem({ genericProductId: id, storeId: null });
        readyForNext({ itemId, name: target.name, where: nameOfStore(null), imagePath: null });
      } else if (target.product.variant_id) {
        // Already in the household's catalogue, so there is nothing to select.
        // Adding it is a local write like any other and works with no signal —
        // sending it through selection would have made a product they already
        // own unaddable in a shop with no reception.
        const owned = await db.product_variants.get(target.product.variant_id);
        if (!owned) throw new Error('variant missing locally');
        const already = existingRow(owned.generic_product_id, owned.id);
        if (already) {
          await addAnother(already, target.product.name, owned.image_path ?? null);
          return;
        }
        const itemId = await addItem({
          genericProductId: owned.generic_product_id,
          variantId: owned.id,
          storeId: owned.store_id,
        });
        readyForNext({
          itemId,
          name: target.product.name,
          where: nameOfStore(owned.store_id),
          imagePath: owned.image_path ?? null,
        });
      } else {
        // Selection needs the network: it is what fetches the EAN and records a
        // first price (API §4.4). You have just searched the shops, so you have
        // a connection — and if it fails, the product can still be added by
        // hand below.
        const created = await api<CreateVariantResponse>('/api/v1/variants', {
          method: 'POST',
          body: {
            id: newId(),
            generic_id: newId(),
            store: target.product.store,
            external_id: target.product.external_id,
            name: target.product.name,
            brand: target.product.brand,
            size_value: target.product.size_value,
            size_unit: target.product.size_unit,
            price_cents: target.product.price_cents,
            promo_price_cents: target.product.promo_price_cents,
            promo_label: target.product.promo_label,
            image_url: target.product.image_url,
          },
        });
        // A live hit belongs to the shop it was found in — there was never a
        // question to ask here.
        const storeId = storeOf(target.product.store)?.id ?? null;
        const itemId = await addItem({
          genericProductId: created.generic_product.id,
          variantId: created.variant.id,
          storeId,
        });
        readyForNext({
          itemId,
          name: created.generic_product.name,
          where: nameOfStore(storeId),
          // The bytes are fetched on demand, so this is a path the phone can
          // ask for even though nothing has ever requested it before.
          imagePath: target.product.image_path,
        });
        // Pull the new rows straight back so the list shows the real names.
        void sync();
      }
    } catch (err) {
      setProblem(
        isOffline(err)
          ? 'Daar is verbinding voor nodig. Voeg het zelf toe — dat synchroniseert later.'
          : 'Toevoegen is niet gelukt. Probeer het nog eens.',
      );
      setBusy(false);
    }
  }

  const typing = query.trim().length > 0;

  /** Suggestions narrowed to organic when the filter is on. */
  const visibleSuggestions = organicOnly
    ? suggestions.filter((s) => genericOrganic.get(s.generic_product_id))
    : suggestions;

  /**
   * Whether to offer the organic filter at all: only once there is something on
   * the screen it could narrow. Keyed on the *unfiltered* content, so turning it
   * on and emptying a section does not also take away the control to turn it off.
   */
  const showOrganicFilter =
    typing || (catalogue ? catalogue.history.length > 0 || suggestions.length > 0 : false);

  /**
   * One row of the live section: a bundle of one is an ordinary result row, a
   * bundle of several is one product with a price per shop.
   *
   * A function rather than an inline `.map` body because the live results are
   * rendered in two places now — what was asked for, and what the shops
   * answered with instead — and two copies of this would be two rows that
   * drifted apart.
   */
  /**
   * One card on a shop's shelf: a big picture, the shop's own title, size and
   * price. One tap adds that shop's product, as every live row always has.
   *
   * **The only thing it ever says about other shops is a cheaper price**
   * (asked for 2026-10-17: "don't clutter"). Cheapest here, or also sold
   * elsewhere for the same, is not something to act on, so it is not drawn.
   * "Goedkoper bij …" is: it is the one fact that would change the pick, and
   * it is only claimed when `bundleByProduct` matched the same product and
   * the other shop's price is strictly lower — never on a tie, never against
   * a shop that publishes no price.
   */
  function renderShelfCard(item: LiveResultDto, cheaper: Cheaper | null) {
    const price = formatCents(item.promo_price_cents ?? item.price_cents);
    const shop = storeName(item.store);
    return (
      <button
        key={`${item.store}-${item.external_id}`}
        type="button"
        className="tile shelf-card"
        disabled={busy}
        aria-label={`${item.name} bij ${shop} toevoegen — ${
          price ?? (item.in_store_only ? 'alleen in de winkel, geen online prijs' : 'geen prijs bekend')
        }${item.organic ? ', biologisch' : ''}${
          cheaper ? `; goedkoper bij ${storeName(cheaper.store)}: ${formatCents(cheaper.price)}` : ''
        }`}
        onClick={() => void commit({ kind: 'live', product: item })}
      >
        <span className="tile-art">
          <ProductThumb src={item.image_path} name={item.name} size="tile" />
          {/* Not on an all-organic shop's shelf: there the shelf head says it
              once, rather than fourteen identical leaves saying it again. */}
          {item.organic && !isOrganicStore(item.store) && <OrganicLeaf />}
        </span>
        <span className="tile-name">{shelfTitle(item.name, item.store, item.size_label)}</span>
        {/* No "alleen in de winkel" for a priceless Lidl card: the household
            knows Lidl publishes no online price (2026-10-17). With neither a
            size nor a price there is no footer at all, rather than an empty
            band the height of one. */}
        {(item.size_label || price) && (
          <span className="shelf-meta">
            <span className="tile-sub">{item.size_label}</span>
            {price && <span className="shelf-price">{price}</span>}
          </span>
        )}
        {cheaper && (
          <span className="shelf-cheaper">
            Goedkoper bij {storeName(cheaper.store)} · {formatCents(cheaper.price)}
          </span>
        )}
      </button>
    );
  }

  function renderBundle(bundle: Bundle<LiveResultDto>) {
    const lead = bundle.lead;
    const key = `${lead.store}-${lead.external_id}`;
    /*
     * What the row is called, which is not the same question as which member
     * ranked first. Live, `Nutella 600 g` outranked `Nutella Hazelnootpasta`,
     * so the row was titled with the less informative of the two and printed a
     * size that is already on the line below it.
     */
    const title = bundle.title;
    /*
     * The same product in another package is on this page — a 2 L bottle and a
     * 4 × 500 ml pack. They are deliberately not one row and their prices are
     * not comparable; saying so is more useful than rendering them as two
     * unrelated products that happen to look alike.
     */
    const otherPack =
      bundle.family && (sections.families.get(bundle.family) ?? 0) > 1
        ? 'ook in een andere verpakking'
        : null;

    /*
     * One shop found it, so there is nothing to compare and nothing to
     * choose between: the whole row is the button it has always been.
     */
    if (bundle.offers.length === 1) {
      return (
        <button
          key={key}
          className="result"
          disabled={busy}
          onClick={() => void commit({ kind: 'live', product: lead })}
        >
          {/* The leaf on the packshot corner, the same marker the household's
              own tiles carry — a biologisch hit is recognisable at a glance
              rather than only in the grey sub-line below. */}
          <span className="thumb-wrap">
            <ProductThumb src={lead.image_path} name={title} />
            {lead.organic && <OrganicLeaf size="thumb" />}
          </span>
          <span className="row-main">
            <span className="row-name">{title}</span>
            <span className="row-sub">
              {[
                storeName(lead.store),
                lead.size_label,
                lead.organic ? 'biologisch' : null,
                lead.in_catalog ? 'al van jullie' : null,
                otherPack,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </span>
          {/* No price, and no sentence in its place: "alleen in de winkel" used
              to stand here for Lidl's range, which publishes no online price,
              and the household asked for it to go (2026-10-17) — they know
              that about Lidl, and it was the same words on nearly every row. */}
          {lead.price_cents !== null && (
            <span className="price">
              {formatCents(lead.promo_price_cents ?? lead.price_cents)}
              {lead.unit_price_cents !== null && (
                <small>
                  {formatCents(lead.unit_price_cents)}/{lead.unit}
                </small>
              )}
            </span>
          )}
        </button>
      );
    }

    /*
     * The same product in more than one shop: named once, priced per
     * shop, cheapest first.
     *
     * The row itself stops being a button here, because "add this"
     * is no longer one action — every price is its own, and tapping
     * one adds *that shop's* product. Which is also why the shop is
     * on the price and not on the sub-line: it is what is being
     * chosen rather than something being reported.
     */
    /*
     * The photograph of any one member is a photograph of all of
     * them — it is the same jar — so the row takes the first one
     * that exists rather than the lead's. Half these shops publish
     * no picture for a given product, and a lettered square beside
     * a name three shops have a packshot of reads as a broken image
     * rather than as a product without one.
     */
    const picture = [lead, ...bundle.offers.map((o) => o.item)].find((i) => i.image_path);

    /*
     * The unit price of the cheapest shop, which is the one thing a
     * bundled row would otherwise lose that an ordinary row has.
     * Within a bundle it says nothing — every member is the same
     * size, so it ranks the shops in exactly the order the prices
     * already did — but *between* rows it is the whole comparison:
     * this is what says the 825g jar beats the 600g one.
     */
    const cheapestOffer = bundle.offers[0]!;
    const perUnit =
      cheapestOffer.item.unit_price_cents !== null
        ? `vanaf ${formatCents(cheapestOffer.item.unit_price_cents)}/${cheapestOffer.item.unit}`
        : null;

    return (
      <div key={key} className="result bundle">
        {/* One leaf for the bundle: organic is a property of the product, so if
            any shop's offer is biologisch the jar is, and the marker sits on the
            single shared packshot — matching the "biologisch" token below it. */}
        <span className="thumb-wrap">
          <ProductThumb src={picture?.image_path ?? null} name={title} />
          {bundle.offers.some((o) => o.item.organic) && <OrganicLeaf size="thumb" />}
        </span>
        <span className="row-main">
          <span className="row-name">{title}</span>
          <span className="row-sub">
            {/* Not "bij 2 winkels": the shops are named on the
                prices immediately below, and counting them again is
                a line of text that tells nobody anything. */}
            {[
              lead.size_label,
              perUnit,
              bundle.offers.some((o) => o.item.organic) ? 'biologisch' : null,
              bundle.offers.some((o) => o.item.in_catalog) ? 'al van jullie' : null,
              otherPack,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
          <span className="offers">
            {bundle.offers.map((offer) => {
              const cheapest = offer.store === bundle.cheapest;
              const price = offer.price_cents === null ? null : formatCents(offer.price_cents);
              return (
                <button
                  key={offer.store}
                  className={`offer${cheapest ? ' cheapest' : ''}`}
                  disabled={busy}
                  /*
                   * Spelled out, because the visible chip is three
                   * words of shorthand sitting inside a row whose
                   * name is somewhere else entirely. Read aloud on
                   * its own, "Jumbo €5,79" is not an answer to what
                   * this button does.
                   */
                  aria-label={`${title} bij ${storeName(offer.store)} toevoegen${
                    price
                      ? ` — ${price}`
                      : offer.item.in_store_only
                        // The shop's own reason, not our shrug: "we don't know"
                        // and "they don't publish one" are different facts, and
                        // only the second tells you the shelf still has it.
                        ? ' — alleen in de winkel, geen online prijs'
                        : ' — geen prijs bekend'
                  }${cheapest ? ', goedkoopst' : ''}`}
                  onClick={() => void commit({ kind: 'live', product: offer.item })}
                >
                  <span className="offer-store">{storeName(offer.store)}</span>
                  {/* A shop that publishes no price online — most of
                      Lidl's food — says so rather than showing a gap
                      that reads as free. */}
                  <span className="offer-price">{price ?? '—'}</span>
                  {/* Never colour alone: the cheapest chip is also
                      the only one that says why it looks different. */}
                  {cheapest && <span className="offer-flag">goedkoopst</span>}
                </button>
              );
            })}
          </span>
        </span>
      </div>
    );
  }

  return (
    <>
      <header className="topbar">
        <Link className="back btn btn-quiet" to="/boodschappen">
          ‹ Terug
        </Link>
        <h1>Toevoegen</h1>
      </header>

      <main className="stack">
        <input
          ref={field}
          type="search"
          autoFocus
          // An example, not a question. It teaches the granularity the rest of
          // the app depends on — a bare noun, which is what the slug and the
          // duplicate check are built around, rather than "2 bruine bollen".
          placeholder={added ? 'Volgende…' : 'bijv. brood'}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        {/*
          Exactly what was typed, as a name — a quiet link directly under the
          field (2026-10-17), not a card and not a tile.

          Where this lives has moved three times, and each place failed one of
          two needs. At the bottom it was buried under up to a hundred shop
          rows, and it is the one option that always works: offline, at a shop
          with no live search, for something no search will ever find. As a
          card at the top it was the biggest, easiest thing to tap, and the
          household wants a real product by default. Here it is always in the
          same spot, above the keyboard where the thumb already is, never moved
          by what has loaded — and visibly secondary to the product tiles.

          It is also no longer a way *around* choosing a product: a name the
          household has bought resolves to its usual product, and anything
          else lands on the card with "Welke wordt het?" offering the shops.
          Same `commit`, same existing-slug check, same offline capability.
        */}
        {/* The two tools under the field share one row: how to add by name
            on the left, the organic filter on the right — rather than each
            taking a row of their own above the first result. */}
        {(typing || showOrganicFilter) && (
        <div className="add-tools">
          {typing && (
            <button
              type="button"
              className="btn btn-quiet add-by-name"
              disabled={busy}
              onClick={() => void commit({ kind: 'manual', name: query.trim() })}
            >
              “{query.trim()}” zelf toevoegen
            </button>
          )}

          {/*
            The organic filter. One tap narrows everything below — your own
            products, what you buy, and the shops' answers — to biologisch, and it
            does it on rows already on the screen, so it works with no signal. It
            sticks across searches on purpose, the way the store chips do: "I am
            only buying organic" stays true for the whole trip.
          */}
          {showOrganicFilter && (
            <nav className="chips chips-inline" aria-label="Resultaten filteren">
              <button
                type="button"
                className="chip"
                aria-pressed={organicOnly}
                onClick={() => setOrganicOnly((v) => !v)}
              >
                Biologisch
              </button>
            </nav>
          )}
        </div>
        )}

        {problem && <p className="banner">{problem}</p>}

        {/*
          What was just added, and the two things you might want to say about it
          within the next two seconds.

          It used to be a one-line strip offering only undo, which left "I want
          two of those" as a trip out to the list, a press-and-hold and a sheet
          — for a change decided while the picture you tapped is still on the
          screen. The stepper is one tap for the case that is nearly all of
          them, and the note is the other thing that is only ever remembered
          here ("de kleine verpakking").

          It sits directly under the field on purpose. The keyboard is up, so
          the bottom half of the screen is gone and a snackbar pinned there
          would be under it; this is the only strip of the screen that is
          reliably both visible and near the thumb. It also still disappears the
          moment the next word is typed — which is the honest dismissal, because
          typing *is* having moved on.
        */}
        {added && !typing && addedRow !== null && (
          <div className="added">
            <div className="added-head">
              <ProductThumb src={added.imagePath} name={added.name} />
              {/* Focus stays in the search field so the next item can be typed,
                  which means nothing announces that the add happened at all
                  unless this says so. */}
              <span className="added-said" role="status" aria-live="polite">
                <span className="added-name">{added.name}</span>
                <span className="row-sub">
                  {[
                    added.where,
                    addedQuantity > 1 ? `${addedQuantity} stuks` : 'toegevoegd',
                    addedRow?.note,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              {/* Undo belongs with the statement rather than with the controls
                  that change it: it is the escape from the whole card, and next
                  to **+** it would be two opposite intentions a thumb's width
                  apart. */}
              <button
                className="btn btn-quiet added-undo"
                onClick={() => {
                  const target = added;
                  setAdded(null);
                  void removeItem(target.itemId);
                }}
              >
                Ongedaan maken
              </button>
            </div>

            <div className="added-actions">
              <span className="qty">
                <button
                  aria-label={`Eén ${added.name} minder`}
                  // One fewer than one is a removal, and there is already a
                  // control for that with the word on it. Two buttons that both
                  // delete, one of them wordlessly, is worse than one.
                  disabled={addedQuantity <= 1}
                  onClick={() => void adjustQuantity(added.itemId, -1)}
                >
                  −
                </button>
                {/* The number itself, because the buttons' names cannot say
                    what the count became and this is the one screen where the
                    person is not looking at the list. */}
                <span aria-live="polite">{addedQuantity}</span>
                <button
                  aria-label={`Eén ${added.name} meer`}
                  onClick={() => void adjustQuantity(added.itemId, +1)}
                >
                  +
                </button>
              </span>

              {!noting && (
                <button
                  className="btn btn-quiet"
                  onClick={() => {
                    setNoteText(addedRow?.note ?? '');
                    savedNote.current = addedRow?.note ?? '';
                    setNoting(true);
                  }}
                >
                  {addedRow?.note ? 'Notitie wijzigen' : 'Notitie'}
                </button>
              )}
            </div>

            {visiblePicks && (
              <div className="added-picks">
                <span className="row-sub">Welke wordt het?</span>
                <div className="added-pick-row">
                  {visiblePicks.map((option) => {
                    const where = storeName(option.store);
                    const price = formatCents(option.priceCents);
                    return (
                      <button
                        key={option.key}
                        type="button"
                        className="added-pick"
                        disabled={choosing}
                        aria-label={`${option.name} bij ${where}${price ? `, ${price}` : ''} kiezen`}
                        onClick={() => void choosePick(option)}
                      >
                        <ProductThumb src={option.imagePath} name={option.name} />
                        <span className="added-pick-name">{option.name}</span>
                        <span className="row-sub">{[where, price].filter(Boolean).join(' · ')}</span>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="btn btn-quiet added-pick-any"
                  disabled={choosing}
                  onClick={() => void anyStorePick()}
                >
                  Maakt niet uit
                </button>
              </div>
            )}

            {noting && (
              <div className="added-note">
                <input
                  type="text"
                  autoFocus
                  aria-label={`Notitie bij ${added.name}`}
                  placeholder="bijv. de kleine verpakking"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  onBlur={commitNote}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitNote();
                  }}
                />
                {/* `preventDefault` on the press, so the field does not blur out
                    from under the button and take it off the screen before the
                    tap that is already happening can land on it. */}
                <button
                  className="btn"
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={commitNote}
                >
                  Klaar
                </button>
              </div>
            )}
          </div>
        )}

        {/* An empty box is the one moment somebody has said "I want to add
            something" and there is nothing else on the screen. Both of these
            read from Dexie, so they are here with no signal too. */}
        {!typing && visibleSuggestions.length > 0 && (
          <>
            <p className="section-label">Dit heb je waarschijnlijk nodig</p>
            <div className="grid">
              {visibleSuggestions.map((suggestion) => {
                const state = tileState(
                  suggestion.generic_product_id,
                  suggestion.suggested_variant_id,
                  suggestion.name,
                );
                return (
                  <Tile
                    key={suggestion.generic_product_id}
                    name={suggestion.name}
                    sub={suggestion.reason}
                    imagePath={genericImages.get(suggestion.generic_product_id) ?? null}
                    organic={genericOrganic.get(suggestion.generic_product_id) ?? false}
                    label={state.label}
                    disabled={busy || state.disabled}
                    badge={state.badge}
                    onClick={() => void accept(suggestion)}
                  />
                );
              })}
            </div>
          </>
        )}

        {!typing && recent.length > 0 && (
          <>
            <p className="section-label">Recent gekocht</p>
            <div className="grid">
              {recent.map(({ generic }) => {
                const picture = genericImages.get(generic.id) ?? null;
                // Recent gekocht is a palette of the things the household buys,
                // so a tile here reads and behaves like a search result rather
                // than a list row: no count drawn on the picture you keep
                // tapping, and a tap is simply one more. The running total lives
                // on the card and the list, where it means something — not on a
                // tile whose whole job is "add another of this". The one state
                // kept is the wall a search tile also puts up: with two rows for
                // this product already (bought at two shops), there is no saying
                // which "one more" meant, so the tile stays inert.
                const ambiguous = (listed.get(generic.id) ?? []).length > 1;
                return (
                  <Tile
                    key={generic.id}
                    name={generic.name}
                    sub={null}
                    imagePath={picture}
                    organic={!!generic.organic}
                    label={`${generic.name} toevoegen`}
                    disabled={busy || ambiguous}
                    badge={ambiguous ? 'op de lijst' : null}
                    onClick={() =>
                      void commit({
                        kind: 'local',
                        genericId: generic.id,
                        variantId: null,
                        storeId: null,
                        label: generic.name,
                        imagePath: picture,
                      })
                    }
                  />
                );
              })}
            </div>
          </>
        )}

        {ownResults.length > 0 && (
          <>
            <p className="section-label">Jullie producten</p>
            <div className="grid">
              {ownResults.map(({ generic, variant }) => {
                const picture = variant?.image_path ?? genericImages.get(generic.id) ?? null;
                const state = tileState(generic.id, variant?.id ?? null, variant?.name ?? generic.name);
                return (
                <Tile
                  key={`${generic.id}-${variant?.id ?? 'g'}`}
                  name={generic.name}
                  // A generic and one of its variants are two tiles with the
                  // same name on them, and the distilled label is often null —
                  // "Havermelk 1L" under "Havermelk" distils to nothing, which
                  // is right on the list and useless here. So the variant tile
                  // says which one it is by size and shop, and the generic tile
                  // says where the household usually buys it.
                  sub={
                    variant
                      ? [
                          variantLabel({
                            shortName: variant.short_name,
                            name: variant.name,
                            brand: variant.brand,
                            sizeValue: variant.size_value,
                            sizeUnit: variant.size_unit,
                            genericName: generic.name,
                          }),
                          sizeLabel(variant.size_value, variant.size_unit),
                          stores.find((s) => s.id === variant.store_id)?.name,
                          variant.organic || generic.organic ? 'biologisch' : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')
                      : generic.organic
                        ? 'Biologisch'
                        : null
                  }
                  // The generic row borrows a picture from one of its own
                  // variants — it is the same product, and a lettered square
                  // next to a photograph of the identical thing reads as a
                  // broken image rather than as a deliberate abstraction.
                  imagePath={picture}
                  organic={rowIsOrganic({ generic, variant })}
                  label={state.label}
                  disabled={busy || state.disabled}
                  badge={state.badge}
                  onClick={() =>
                    void commit({
                      kind: 'local',
                      genericId: generic.id,
                      variantId: variant?.id ?? null,
                      storeId: variant?.store_id ?? null,
                      label: generic.name,
                      imagePath: picture,
                    })
                  }
                  // Press and hold to take it out of this section — the same
                  // gesture the list uses for "everything but buying it".
                  {...(variant && {
                    onLongPress: () => setHiding(hideTarget(generic.name, variant)),
                    corner: {
                      label: `${generic.name} verbergen`,
                      glyph: '⋯',
                      onClick: () => setHiding(hideTarget(generic.name, variant)),
                    },
                  })}
                />
                );
              })}
            </div>
          </>
        )}

        {searching && <p className="empty">Winkels raadplegen…</p>}

        {/*
          Live hits stay rows, and that is not an inconsistency. Everything
          above is the household's own catalogue, where the product is known and
          the picture is the fastest way to point at it. This section is four
          shops answering the same question at four prices — it *is* a
          comparison, which is the one place a price belongs.
        */}
        {live && (
          <>
            {shelves.some((shelf) => shelf.items.length > 0) && (
              <p className="section-label">In de winkels</p>
            )}

            {/*
              One shelf per shop, scrolled sideways (2026-10-17). A shop's
              shelf is what a person standing in that shop reads, so the shop
              filter chips this replaced are no longer needed: the row *is* the
              filter. An unreachable shop keeps its place in the order and says
              so there, so its absence is never read as "they don't sell it".
            */}
            {shelves.map((shelf) => (
              <section
                key={shelf.code}
                className={`shelf${shelf.status && !shelf.status.ok ? ' shelf-down' : ''}`}
                aria-label={storeName(shelf.code)}
              >
                {/*
                  The shop's own mark at icon size and its name in ink: the
                  heading of what follows, not a coloured pill louder than the
                  products under it. Beside it only what is worth saying — why a
                  shop is missing, or that its whole range is organic — never a
                  count nobody acts on.
                */}
                <div className="shelf-head">
                  {shopLogo(shelf.code) && (
                    <img className="shop-logo" src={shopLogo(shelf.code)!} alt="" width={22} height={22} />
                  )}
                  <h2 className="shelf-title">{storeName(shelf.code)}</h2>
                  {shelf.status && !shelf.status.ok ? (
                    <span className="row-sub wraps">{shopProblem(shelf.status.reason)}</span>
                  ) : isOrganicStore(shelf.code) ? (
                    <span className="row-sub">alles biologisch</span>
                  ) : null}
                </div>
                {shelf.items.length > 0 && (
                  <div className="shelf-row">
                    {shelf.items.map(({ item, cheaper }) => renderShelfCard(item, cheaper))}
                  </div>
                )}
              </section>
            ))}

            {/*
              What the shops answered with instead.

              Lidl and Ekoplaza reply to a word they do not stock with an
              arbitrary page of whatever is on promotion, so a search for
              "nutella" comes back with a melon and an air fryer in it. Under
              its own heading that reads as the shop having nothing; mixed into
              the list above it read as this app being broken.

              Still every row, still one tap away. The heading appears only when
              the model actually judged something, so with no key, a spent
              ceiling or a timeout there is no heading and no second list.
            */}
            {/* Folded by default (2026-10-17): this is where a sandbox and a
                kettle land for "pindakaas", and open it stood between every
                answer and the bottom of the screen. Folded is not filtered —
                the count says what is there and one tap shows every row. */}
            {sections.aside.length > 0 && (
              <>
                <button
                  type="button"
                  className="section-label section-toggle"
                  aria-expanded={asideOpen}
                  onClick={() => setAsideOpen((v) => !v)}
                >
                  Misschien niet wat je zocht ({sections.aside.length})
                  <span aria-hidden="true">{asideOpen ? '▴' : '▾'}</span>
                </button>
                {asideOpen && sections.aside.map(renderBundle)}
              </>
            )}

          </>
        )}

      </main>

      {/*
        Hiding from "Jullie producten" (migration 041). It says exactly what it
        does and does not touch, because "remove" next to a product reads as
        "take it off my list" or "forget I ever bought it" — it is neither.
      */}
      {hiding && (
        <>
          <div className="sheet-backdrop" onClick={() => setHiding(null)} aria-hidden="true" />
          <div className="sheet" role="dialog" aria-modal="true" aria-label={`${hiding.name} verbergen`}>
            <button className="sheet-grip" aria-label="Sluiten" onClick={() => setHiding(null)} />
            <h2>{hiding.name}</h2>
            <p className="sheet-note">
              Verdwijnt uit Jullie producten{hiding.where ? ` (${hiding.where})` : ''}. Je lijst en wat
              je eerder kocht blijven zoals ze zijn, en kies je het later weer in de winkel, dan komt
              het terug.
            </p>
            <div className="sheet-row">
              <button type="button" className="btn btn-quiet" onClick={() => setHiding(null)}>
                Annuleren
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  const { variantId } = hiding;
                  setHiding(null);
                  void hideVariant(variantId);
                }}
              >
                Verbergen
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
